import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import { useProgress, type StepKey } from '../state/ProgressContext';

interface Detection {
  countryCode: string;
  countryName: string;
  eligibility: 'PENDING' | 'ELIGIBLE' | 'BLOCKED';
  confirmed: boolean;
}
interface CountryOption { code: string; name: string }
interface FieldMarket { id: string; name: string }

const STEP_ROUTES: Record<StepKey, string> = {
  LOCATION: '/onboarding/country', INITIAL_NOTICE: '/onboarding/notice', APPLICATION_QUESTIONS: '/onboarding/application',
  APPLICATION_REVIEW: '/onboarding/application', SALES_RESOURCES: '/onboarding/resources', AGREEMENT: '/onboarding/agreement',
  TAX_FORM: '/onboarding/tax-form', DOCUMENT_REVIEW: '/onboarding/document-review', ONBOARDING_WELCOME: '/onboarding/training', GET_LEADS: '/onboarding/training', PREPARE: '/onboarding/training', MAKE_THE_CALL: '/onboarding/training',
  FOLLOW_UP: '/onboarding/training', COMPLETE: '/onboarding/training',
};

export function CountryConfirm() {
  const [detection, setDetection] = useState<Detection | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [countryInput, setCountryInput] = useState('');
  const [markets, setMarkets] = useState<FieldMarket[]>([]);
  const [marketId, setMarketId] = useState('');
  const navigate = useNavigate();
  const { progress, refresh } = useProgress();
  // The field position is only offered in the markets Krovance covers, so a field applicant
  // picks theirs here, with the country. The server enforces the same rule on this step and on
  // every step after it - this is the UI for a restriction, not the restriction itself.
  const marketRequired = Boolean(progress?.market?.required);

  useEffect(() => {
    Promise.all([
      api.get<{ detection: Detection }>('/api/country/detect'),
      api.get<{ countries: CountryOption[] }>('/api/country/options'),
      api.get<{ markets: FieldMarket[] }>('/api/country/markets'),
    ])
      .then(([response, options, marketOptions]) => { setDetection(response.detection); setCountries(options.countries); setCountryInput(response.detection.countryName); setMarkets(marketOptions.markets); })
      .catch((loadError) => setError(loadError instanceof ApiError ? loadError.message : 'Could not detect your country.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (progress?.market?.id) setMarketId(progress.market.id); }, [progress?.market?.id]);

  async function handleConfirm() {
    const selected = countries.find((country) => country.name.toLocaleLowerCase() === countryInput.trim().toLocaleLowerCase() || country.code === countryInput.trim().toUpperCase());
    if (!selected) { setError('Select a valid country from the list.'); return; }
    if (marketRequired && !marketId) { setError('Select the area you live in or near to continue.'); return; }
    setConfirming(true); setError(null);
    try {
      await api.post('/api/country/confirm', { countryCode: selected.code, ...(marketRequired ? { market: marketId } : {}) });
      await refresh();
      const current = await api.get<{ currentStep: StepKey }>('/api/progress');
      navigate(STEP_ROUTES[current.currentStep === 'LOCATION' ? 'INITIAL_NOTICE' : current.currentStep], { replace: true });
    } catch (confirmError) {
      setError(confirmError instanceof ApiError ? confirmError.message : 'Could not confirm your country.');
    } finally { setConfirming(false); }
  }

  if (loading) return <div className="center-screen"><div className="loading-state">Detecting your country…</div></div>;

  return <div className="center-screen"><div className="card login-card">
    <h1>Confirm Your Country</h1>
    <p>We detected your country from your current IP address.</p>
    {error && <div className="alert error" role="alert">{error}</div>}
    {detection && <>
      <div className="form-field"><label htmlFor="country-selection"><strong>Country</strong></label><input id="country-selection" list="country-options" value={countryInput} onChange={(event) => setCountryInput(event.target.value)} placeholder="Start typing your country" autoComplete="country-name" /><datalist id="country-options">{countries.map((country) => <option key={country.code} value={country.name}>{country.code}</option>)}</datalist></div>
      {marketRequired && <div className="form-field">
        <label htmlFor="field-market"><strong>Which area are you in?</strong></label>
        <select id="field-market" value={marketId} onChange={(event) => setMarketId(event.target.value)}>
          <option value="">Select your area</option>
          {markets.map((market) => <option key={market.id} value={market.id}>{market.name}</option>)}
        </select>
        <p className="field-market-note">This position is worked in person, so it is open only in the areas listed above. If you do not live in or near one of them, you are not eligible for it right now.</p>
      </div>}
      <h2 className="country-confirm-question">Is this correct?</h2>
      <div className="alert warning">Make sure your VPN, proxy, or privacy relay is disabled before confirming so we can detect your actual location.</div>
      <div className="alert info">{(countries.find((country) => country.name.toLocaleLowerCase() === countryInput.trim().toLocaleLowerCase() || country.code === countryInput.trim().toUpperCase())?.code ?? detection.countryCode) === 'US' ? 'You will receive Form W-9.' : 'You will receive Form W-8BEN for foreign individuals.'}</div>
    </>}
    <button className="btn-primary" style={{ width: '100%', marginTop: 12 }} onClick={handleConfirm} disabled={!countryInput.trim() || (marketRequired && !marketId) || confirming}>{confirming ? 'Confirming…' : 'Yes, this is correct'}</button>
    {!detection && error && <button className="btn-secondary" style={{ width: '100%', marginTop: 10 }} onClick={() => window.location.reload()}>Try detection again</button>}
  </div></div>;
}
